import React from 'react';
import classNames from 'classnames';
import I18n from 'i18n-js';

import FacetOption from './facet_option';
import FilterLink from './filter_link';
import { FilterOption } from './search_filters';
import { MParticleEventName } from '../elog/mparticle_types';
import { trackEvent } from '../elog/mparticle_tracker';
import ProximityFacet, { PROXIMITY_PARAM } from './proximity_facet';
import { formatExperimentsForEventAttributes } from '../elog/mparticle';
import { useUser } from '../user_hooks';

interface IExternalProps {
  option: FilterOption;
  checkbox?: boolean;
  children?: JSX.Element;
  childActive?: boolean;
  trackingQuery?: string;
}

type IProps = IExternalProps;

function onLinkClick(evt, zeroCount: boolean) {
  if (zeroCount) {
    evt.preventDefault();
  }
}

function clickedFilterLink(props: IProps, user) {
  trackEvent({
    componentName: 'SelectFacetOption',
    eventName: MParticleEventName.ClickedSearchFilter,
    displayStyle: props.option.optionValue || props.option.trackingValue || 'default',
    selected: !props.option.selected,
    parent: props.option.paramName,
    query: props.trackingQuery,
    experiments: formatExperimentsForEventAttributes(user, []),
  });
}

function linkParams(props: IProps) {
  const useUnset = props.option.selected && props.checkbox;
  const values = useUnset ? props.option.unsetValues : props.option.setValues;

  return { [props.option.paramName]: values };
}

function renderCount(props: IProps) {
  if (!props.option.count) {
    return null;
  }

  return (
    <div className="facet__option__count">
      ({I18n.toNumber(props.option.count.value, { precision: 0 })})
    </div>
  );
}

export function SelectFacetOption(props: IProps) {
  const { option } = props;
  const user = useUser();
  const linkClass = classNames({
    facet__link: true,
    'facet__link--checkbox': props.checkbox,
    'facet__link--active': option.selected,
  });
  const zeroCount = option.count?.value === 0;
  const displayProximityFilterFacet = option.paramName === PROXIMITY_PARAM;
  const displayProximitySort = option.optionValue.includes(PROXIMITY_PARAM);

  if (displayProximityFilterFacet || displayProximitySort) {
    return (
      <ProximityFacet
        option={option}
        linkParams={linkParams(props)}
        trackInteraction={() => clickedFilterLink(props, user)}
        inSortContext={displayProximitySort}
      />
    );
  }

  return (
    <FacetOption
      active={option.selected}
      childActive={props.childActive}
      disabled={zeroCount}
      paramName={option.paramName}
    >
      <FilterLink
        onClick={(evt) => onLinkClick(evt, zeroCount)}
        className={linkClass}
        params={linkParams(props)}
        data-checked={option.selected || null}
        data-disabled={zeroCount || null}
        rel="nofollow"
        trackInteraction={() => clickedFilterLink(props, user)}
      >
        <div>
          <span className="mr-space">{option.name}</span>
          {renderCount(props)}
        </div>
      </FilterLink>
      {props.children}
    </FacetOption>
  );
}
